/**
 * AllysListingCard - Allys-themed Listing Card Component
 * 
 * Wraps the core ListingCard with Allys-specific styling:
 * - Navy (#243462) and Gold (#BBA339) color scheme
 * - Inter font family
 * - Consistent border and shadow styles
 */

'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Bed, Bath, Maximize, MapPin, Calendar, User, Clock, Gavel } from 'lucide-react';
import { WishlistButton } from '@/components/customer/WishlistButton';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import type { ListingCardData } from '@/components/listings/types';
import {
  getListingTypeLabel,
  getListingStatusLabel,
} from '@/components/listings/types';
import { FeaturedBadge } from '@/components/listings/FeaturedBadge';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import type { FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';
import { AllysAgentContactPopover } from './AllysAgentContactPopover';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

export interface AllysListingCardProps {
  listing: ListingCardData;
  href: string;
  variant?: 'default' | 'compact' | 'featured';
  showAgent?: boolean;
  showAgentAvatar?: boolean;
  showStatus?: boolean;
  showFeaturedBadge?: boolean;
  showWishlistButton?: boolean;
  wishlistEntityType?: 'PROPERTY' | 'LISTING';
  locale?: string;
  messages?: Record<string, unknown>;
  className?: string;
}

// Allys-themed colors for listing types
function getAllysListingTypeColor(type: string): string {
  if (type === 'SALE' || type === 'COMMERCIAL_SALE') {
    return 'bg-[#243462] text-white'; // Allys Navy
  }
  if (type.includes('RENTAL') || type === 'ROOM_RENTAL' || type === 'SUBLET') {
    return 'bg-[#BBA339] text-white'; // Allys Gold
  }
  if (type === 'AUCTION') {
    return 'bg-gradient-to-r from-[#BBA339] to-[#E4CE4A] text-[#243462]';
  }
  if (type === 'LEASE' || type === 'COMMERCIAL_LEASE') {
    return 'bg-[#243462]/80 text-white';
  }
  return 'bg-gray-600 text-white';
}

// Allys-themed colors for listing status
function getAllysListingStatusColor(status: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'bg-gray-500 text-white',
    ACTIVE: 'bg-[#243462] text-white',
    COMING_SOON: 'bg-[#BBA339] text-white',
    UNDER_OFFER: 'bg-amber-500 text-white',
    SOLD: 'bg-red-600 text-white',
    LEASED: 'bg-purple-600 text-white',
    WITHDRAWN: 'bg-gray-600 text-white',
    ARCHIVED: 'bg-gray-400 text-white',
    EXPIRED: 'bg-gray-500 text-white',
  };
  return colors[status] || 'bg-gray-500 text-white';
}

export function AllysListingCard({
  listing,
  href,
  variant = 'default',
  showAgent = false,
  showAgentAvatar = true,
  showStatus = true,
  showFeaturedBadge = true,
  showWishlistButton = true,
  wishlistEntityType = 'PROPERTY',
  locale = 'en',
  messages,
  className = '',
}: AllysListingCardProps) {
  const cardMsg = messages?.card as Record<string, unknown> | undefined;
  const { property, location, listingType, status, price, rentalTerms, auctionDetails, listingAgent, featuredPlacements } = listing;
  
  const wishlistEntityId = wishlistEntityType === 'LISTING' ? listing.listingId : property.id;
  const hasSpecialStatus = ['UNDER_OFFER', 'SOLD', 'LEASED', 'COMING_SOON'].includes(status);
  
  const primaryFeatured = showFeaturedBadge && featuredPlacements 
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;
  
  // Determine if we should use custom display text or format with FormatPrice
  const hasCustomPriceText = !!listing.displayPriceText;
  const isAuction = listingType === 'AUCTION';
  
  const locationString = [
    location?.suburb,
    location?.city,
    location?.district,
    location?.neighbourhood,
    location?.region,
  ].filter(Boolean).join(', ') || location?.name || 'Location TBA';

  const cardClasses = `
    bg-white rounded-2xl overflow-hidden 
    shadow-lg hover:shadow-2xl 
    transition-all duration-500 group
    border border-gray-100 hover:border-[#BBA339]/30
    hover:-translate-y-1
    ${variant === 'compact' ? 'flex flex-row' : ''}
    ${variant === 'featured' ? 'ring-2 ring-[#BBA339]' : ''}
    ${className}
  `.trim();

  return (
    <Link href={href} className="block no-underline text-inherit">
      <div className={`${cardClasses} font-inter`}>
        {/* Image Container */}
        <div className={`relative overflow-hidden bg-gray-100 ${
          variant === 'compact' ? 'w-48 sm:w-64 lg:w-72 flex-shrink-0 min-h-[180px]' : 'w-full aspect-[4/3]'
        }`}>
          {property.primaryImageUrl ? (
            <Image
              src={property.primaryImageUrl}
              alt={property.name}
              fill
              className="object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
              sizes={variant === 'compact' ? '(max-width: 640px) 192px, (max-width: 1024px) 256px, 288px' : '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200">
              <span>{safeGetString(cardMsg?.noImage, 'No Image')}</span>
            </div>
          )}
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          {/* Wishlist Button */}
          {showWishlistButton && (
            <div className="absolute top-3 left-3 z-20">
              <WishlistButton
                entityType={wishlistEntityType}
                entityId={wishlistEntityId}
                entityName={property.name}
                value={price?.amount ?? undefined}
                variant="solid"
                size="sm"
              />
            </div>
          )}
          
          {/* Listing Type Badge -- replaced with "Sold" when listing is sold */}
          <div className={`absolute ${showWishlistButton ? 'top-3 left-12' : 'top-3 left-3'} px-3 py-1.5 text-xs font-bold rounded-full shadow-lg ${
            status === 'SOLD' ? 'bg-gray-700 text-white' : getAllysListingTypeColor(listingType)
          }`}>
            {status === 'SOLD' ? 'Sold' : getListingTypeLabel(listingType)}
          </div>
          
          {/* Featured Badge */}
          {primaryFeatured?.badgeVariant && !hasSpecialStatus && (
            <div className="absolute top-3 right-3 z-10">
              <FeaturedBadge
                variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
                label={primaryFeatured.label}
                size="sm"
              />
            </div>
          )}
          
          {/* Status Badge */}
          {showStatus && hasSpecialStatus && (
            <div className={`absolute top-3 right-3 px-3 py-1.5 text-xs font-bold rounded-full shadow-lg ${getAllysListingStatusColor(status)}`}>
              {getListingStatusLabel(status)}
            </div>
          )}
          
          {/* Featured Badge - below status */}
          {primaryFeatured?.badgeVariant && hasSpecialStatus && (
            <div className="absolute top-12 right-3 z-10">
              <FeaturedBadge
                variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
                label={primaryFeatured.label}
                size="sm"
              />
            </div>
          )}
          
          {/* Auction Date Banner */}
          {listingType === 'AUCTION' && auctionDetails?.auctionDate && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A] text-[#243462] px-3 py-2 flex items-center gap-2">
              <Gavel size={14} />
              <span className="text-xs font-bold">
                Auction: {new Date(auctionDetails.auctionDate).toLocaleDateString(locale, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}
          
          {/* Agent Avatar with Contact Popover */}
          {showAgentAvatar && listingAgent && variant !== 'compact' && (
            <div 
              className={`absolute ${listingType === 'AUCTION' && auctionDetails?.auctionDate ? 'bottom-12' : 'bottom-3'} right-3 z-10`}
            >
              <AllysAgentContactPopover agent={listingAgent}>
                {listingAgent.avatar ? (
                  <div className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full overflow-hidden ring-2 ring-white shadow-lg transition-transform duration-200 hover:scale-110">
                    <Image
                      src={listingAgent.avatar}
                      alt={listingAgent.name}
                      width={44}
                      height={44}
                      className="object-cover object-top w-full h-full"
                    />
                  </div>
                ) : (
                  <div className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full bg-[#243462] ring-2 ring-white shadow-lg flex items-center justify-center transition-transform duration-200 hover:scale-110">
                    <User size={18} className="text-white" />
                  </div>
                )}
              </AllysAgentContactPopover>
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`p-5 flex flex-col gap-2 ${variant === 'compact' ? 'flex-1' : ''}`}>
          {/* Property Type & Location */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {listing.propertyType && (
              <>
                <span className="font-semibold text-[#243462]">{listing.propertyType.name}</span>
                <span>•</span>
              </>
            )}
            <span className="flex items-center gap-1">
              <MapPin size={12} className="text-[#BBA339]" />
              {locationString}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-lg font-bold text-[#243462] line-clamp-2 group-hover:text-[#BBA339] transition-colors">
            {listing.mainHeadline || property.name}
          </h3>

          {/* Description snippet */}
          {property.description && (
            <p className="text-sm text-gray-500 font-inter line-clamp-2 leading-relaxed">
              {property.description}
            </p>
          )}

          {/* Price - uses FormatPrice for automatic currency conversion */}
          <div className="text-xl font-extrabold text-[#243462]">
            {hasCustomPriceText ? (
              // Use custom display text from API (e.g., "Price on Application")
              <span>{listing.displayPriceText}</span>
            ) : isAuction && auctionDetails ? (
              // Auction: show guide price range with currency conversion
              <span className="flex items-center gap-1">
                {auctionDetails.guidePriceMin && (
                  <FormatPrice
                    amount={auctionDetails.guidePriceMin}
                    currency={auctionDetails.currency || DEFAULT_CURRENCY}
                    size="md"
                    priceOnRequestText="Guide TBA"
                  />
                )}
                {auctionDetails.guidePriceMin && auctionDetails.guidePriceMax && (
                  <span className="mx-1">-</span>
                )}
                {auctionDetails.guidePriceMax && !auctionDetails.guidePriceMin && (
                  <span className="mr-1">{safeGetString(cardMsg?.upTo, 'Up to')}</span>
                )}
                {auctionDetails.guidePriceMax && (
                  <FormatPrice
                    amount={auctionDetails.guidePriceMax}
                    currency={auctionDetails.currency || DEFAULT_CURRENCY}
                    size="md"
                    priceOnRequestText=""
                  />
                )}
                {!auctionDetails.guidePriceMin && !auctionDetails.guidePriceMax && (
                  <span>{safeGetString(cardMsg?.guidePriceTBA, 'Guide price TBA')}</span>
                )}
              </span>
            ) : price ? (
              // Standard price with currency conversion
              <span className="flex items-center">
                <FormatPrice
                  amount={price.amount}
                  currency={price.currency}
                  frequency={price.frequency ?? null}
                  size="md"
                  priceOnRequestText={safeGetString(cardMsg?.contactForPrice, 'Price on request')}
                />
              </span>
            ) : (
              <span>{safeGetString(cardMsg?.contactForPrice, 'Price on request')}</span>
            )}
            {rentalTerms?.furnishedStatus && rentalTerms.furnishedStatus !== 'UNFURNISHED' && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({rentalTerms.furnishedStatus.toLowerCase().replace('_', ' ')})
              </span>
            )}
          </div>

          {/* Key Details */}
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 py-2 px-3 bg-gray-50 rounded-xl border border-gray-100">
            {property.bedrooms !== null && property.bedrooms > 0 && (
              <div className="flex items-center gap-1.5">
                <Bed size={14} className="text-[#243462]" />
                <span className="font-semibold">{property.bedrooms}</span>
                <span className="text-xs text-gray-500">{safeGetString(cardMsg?.bedrooms, 'bed')}</span>
              </div>
            )}
            
            {property.bathrooms !== null && property.bathrooms > 0 && (
              <div className="flex items-center gap-1.5">
                <Bath size={14} className="text-[#BBA339]" />
                <span className="font-semibold">{property.bathrooms}</span>
                <span className="text-xs text-gray-500">{safeGetString(cardMsg?.bathrooms, 'bath')}</span>
              </div>
            )}
            
            {property.totalFloorArea !== null && property.totalFloorArea > 0 && (
              <div className="flex items-center gap-1.5">
                <Maximize size={14} className="text-gray-400" />
                <span className="font-semibold">{property.totalFloorArea.toLocaleString()}</span>
                <span className="text-xs text-gray-500">{property.totalFloorAreaUnit?.toLowerCase() || 'sqm'}</span>
              </div>
            )}
          </div>

          {/* Rental-specific info */}
          {rentalTerms && (
            <div className="flex flex-wrap gap-2 text-xs">
              {rentalTerms.minLeaseMonths && (
                <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-full">
                  <Clock size={10} />
                  Min {rentalTerms.minLeaseMonths} months
                </span>
              )}
              {rentalTerms.petPolicy && rentalTerms.petPolicy !== 'NO_PETS' && (
                <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
                  Pets OK
                </span>
              )}
            </div>
          )}

          {/* Available date */}
          {listing.availableDate && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Calendar size={12} className="text-[#BBA339]" />
              <span>
                Available: {new Date(listing.availableDate).toLocaleDateString(locale, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}

          {/* Agent Info (full display) */}
          {showAgent && listingAgent && variant !== 'compact' && (
            <div className="flex items-center gap-3 pt-3 mt-auto border-t border-gray-100">
              <AllysAgentContactPopover agent={listingAgent} position="below">
                {listingAgent.avatar ? (
                  <Image
                    src={listingAgent.avatar}
                    alt={listingAgent.name}
                    width={36}
                    height={36}
                    className="rounded-full object-cover object-top hover:scale-110 transition-transform duration-200"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#243462] flex items-center justify-center hover:scale-110 transition-transform duration-200">
                    <User size={16} className="text-white" />
                  </div>
                )}
              </AllysAgentContactPopover>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#243462] truncate">{listingAgent.name}</p>
                {listingAgent.jobTitle && (
                  <p className="text-xs text-[#BBA339] truncate font-medium">{listingAgent.jobTitle}</p>
                )}
              </div>
            </div>
          )}

          {/* View Details CTA */}
          {variant !== 'compact' && !showAgent && (
            <div className="flex items-center justify-between pt-3 mt-auto border-t border-gray-100">
              <span className="text-sm font-bold text-[#243462] group-hover:text-[#BBA339] transition-colors">{safeGetString(cardMsg?.viewDetails, 'View Details')}</span>
              <span className="w-8 h-8 rounded-full bg-[#243462] text-white flex items-center justify-center transition-all group-hover:bg-[#BBA339] group-hover:translate-x-1">
                →
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default AllysListingCard;
